import { FormField } from '@batiflow/ui';
import { Input, Select, __css, tokens } from '@fluentui/react-components';
import { useTranslation } from 'react-i18next';
import {
  DISCIPLINES,
  DOCUMENT_TYPES,
  PHASE_TYPES,
  isDiscipline,
  isDocumentType,
  isPhaseType,
  type MetadataValues,
  type PhaseType,
} from './naming-model';
import type { Translate } from './model';

const useStyles = __css({"grid":{"mc9l5x":"f13qh94s","Budl1dq":"fyxkby2","Bt984gj":"f1s2louj","i8kkvl":"f1xcgk68","Belr9w4":"f1noh358"}});

/** "APS — Avant-projet sommaire"; a custom phase is just its name. */
export function phaseOptionLabel(t: Translate, phase: PhaseType): string {
  return phase === 'custom'
    ? t('projects:phaseTypes.custom')
    : t('metadata.phaseOption', {
        code: t(`projects:phaseTypes.${phase}`),
        name: t(`projects:phaseNames.${phase}`),
      });
}

export interface MetadataFieldsProps {
  values: MetadataValues;
  onChange: (patch: Partial<MetadataValues>) => void;
  disabled?: boolean;
}

/** Discipline, phase, type, zone and scale of a document: the metadata the naming pattern uses. */
export function MetadataFields({ values, onChange, disabled = false }: MetadataFieldsProps) {
  const { t } = useTranslation('documents');
  const styles = useStyles();
  return (
    <div className={styles.grid}>
      <FormField label={t('metadata.discipline')}>
        <Select
          disabled={disabled}
          value={values.discipline ?? ''}
          onChange={(_event, data) => {
            onChange({ discipline: isDiscipline(data.value) ? data.value : null });
          }}
        >
          <option value="">{t('metadata.notSet')}</option>
          {DISCIPLINES.map((discipline) => (
            <option key={discipline} value={discipline}>
              {t(`ui:components.disciplines.${discipline}`)}
            </option>
          ))}
        </Select>
      </FormField>
      <FormField label={t('metadata.phase')}>
        <Select
          disabled={disabled}
          value={values.phaseType ?? ''}
          onChange={(_event, data) => {
            onChange({ phaseType: isPhaseType(data.value) ? data.value : null });
          }}
        >
          <option value="">{t('metadata.notSet')}</option>
          {PHASE_TYPES.map((phase) => (
            <option key={phase} value={phase}>
              {phaseOptionLabel(t, phase)}
            </option>
          ))}
        </Select>
      </FormField>
      <FormField label={t('metadata.docType')}>
        <Select
          disabled={disabled}
          value={values.docType ?? ''}
          onChange={(_event, data) => {
            onChange({ docType: isDocumentType(data.value) ? data.value : null });
          }}
        >
          <option value="">{t('metadata.notSet')}</option>
          {DOCUMENT_TYPES.map((type) => (
            <option key={type} value={type}>
              {t(`docTypes.${type}`)}
            </option>
          ))}
        </Select>
      </FormField>
      <FormField label={t('metadata.zone')} hint={t('metadata.optional')}>
        <Input
          disabled={disabled}
          maxLength={40}
          value={values.zone}
          onChange={(_event, data) => {
            onChange({ zone: data.value });
          }}
        />
      </FormField>
      <FormField label={t('metadata.scale')} hint={t('metadata.optional')}>
        <Input
          disabled={disabled}
          maxLength={40}
          value={values.scale}
          onChange={(_event, data) => {
            onChange({ scale: data.value });
          }}
        />
      </FormField>
    </div>
  );
}


import "/opt/batiflow/app/apps/web/src/features/documents/metadata-fields.griffel.css";